import { useControls } from 'leva';
import { BufferGeometry, Color, Mesh, MeshBasicMaterial, Spherical, Vector3 } from 'three';

import { Billboard, BillboardProps, Text, TrackballControls } from '@react-three/drei';
import { Canvas, useFrame } from '@react-three/fiber';
import * as React from 'react';

declare namespace Word {
  export type Props = BillboardProps;
}

function Word(props: Word.Props) {
  const { children, ...billboardProps } = props;
  const color = new Color();
  const [hovered, setHovered] = React.useState(false);

  React.useEffect(() => {
    if (hovered) {
      document.body.style.cursor = 'pointer';
    }
    return () => {
      document.body.style.cursor = 'auto';
    };
  });

  const ref = React.useRef<Mesh<BufferGeometry, MeshBasicMaterial>>(null);
  useFrame(() => {
    const text = ref.current;
    text?.material.color.lerp(color.set(hovered ? '#fa2720' : 'white'), 0.1);
  });

  return (
    <Billboard {...billboardProps}>
      <Text
        ref={ref}
        fontSize={2.5}
        letterSpacing={-0.05}
        lineHeight={1}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHovered(true);
        }}
        onPointerOut={() => {
          setHovered(false);
        }}
      >
        {children}
      </Text>
    </Billboard>
  );
}

declare namespace Cloud {
  export type Props = {
    count: number;
    radius: number;
  };
}

function Cloud(props: Cloud.Props) {
  const { count, radius } = props;
  const words = React.useMemo(() => {
    const words: [position: Vector3, text: string][] = [];
    const spherical = new Spherical();
    const phiSpan = Math.PI / (count + 1);
    const thetaSpan = (Math.PI * 2) / count;
    for (let i = 1; i < count + 1; i += 1) {
      for (let j = 0; j < count; j += 1) {
        const phi = phiSpan * i;
        const theta = thetaSpan * j;
        words.push([
          new Vector3().setFromSpherical(spherical.set(radius, phi, theta)),
          `phi: ${phi.toFixed(2)}, theta: ${theta.toFixed(2)}`,
        ]);
      }
    }
    return words;
  }, [count, radius]);
  return words.map(([position, text], index) => (
    <Word key={index} position={position}>
      {text}
    </Word>
  ));
}

export type Props = {};

function App(props: Props) {
  const {} = props;

  const { count, radius, rotation } = useControls({
    // dpr: {
    //   min: 1,
    //   max: 2,
    // },
    // camera_position: {
    //   x: 0,
    //   y: 0,
    //   z: 35,
    // },
    // camera_fov: 90,
    count: 8,
    radius: 20,
    rotation: {
      x: 10,
      y: 10.5,
      z: 10,
    },
  });

  // console.log('App', { camera_position });
  return (
    <Canvas
      style={{
        height: '300px',
        backgroundColor: '#202025',
      }}
      // dpr={[dpr.min, dpr.max]}
      // camera={{
      //   position: [camera_position.x, camera_position.y, camera_position.z],
      //   fov: camera_fov,
      // }}
      dpr={[1, 2]}
      camera={{
        position: [0, 0, 35],
        fov: 90,
      }}
    >
      <fog attach="fog" args={['#202025', 0, 80]} />
      {/* <ambientLight intensity={Math.PI} /> */}
      {/* <pointLight decay={0} position={[10, 10, 5]} /> */}
      {/* <Box args={[1, 1, 1]} /> */}
      <React.Suspense fallback={null}>
        <group rotation={[rotation.x, rotation.y, rotation.z]}>
          <Cloud count={count} radius={radius} />
        </group>
      </React.Suspense>
      <TrackballControls />
    </Canvas>
  );
}

export default React.memo(App);
